import React from "react";

const getCells = row => Array.isArray(row) ? row : Array.isArray(row?.cells) ? row.cells : [];

const renderCell = cell => {
  if (!cell) return null;
  if (React.isValidElement(cell.node)) return cell.node;
  if (cell.node !== undefined && cell.node !== null) return cell.node;
  if (cell.raw !== undefined && cell.raw !== null) return cell.raw;
  return null;
};

export const BasicTable = ({data = [], additionalFilter = {}, onAdditionalFilterChange = () => {}, filter = {}, onFilterChange = null}) => {

  const headers = React.useMemo(() => {
    const map = new Map();
    data.forEach(row => getCells(row).forEach(cell => {
      if (cell?.key && !map.has(cell.key)) map.set(cell.key, {key: cell.key, name: cell.name || cell.key, sortKey: cell.sortKey || null});
    }));
    return Array.from(map.values());
  }, [data]);

  const filterableKeys = React.useMemo(() => {
    const headerKeys = new Set(headers.map(h => h.key));
    return Object.keys(additionalFilter || {}).filter(key => headerKeys.has(key));
  }, [additionalFilter, headers]);

  const filteredData = React.useMemo(() => {
    return data.filter(row => {
      const cells = getCells(row);
      return filterableKeys.every(key => {
        const filterValue = additionalFilter[key];
        if (!filterValue) return true;
        const cell = cells.find(c => c.key === key);
        if (!cell) return true;
        return String(cell.raw ?? "").toLowerCase().includes(String(filterValue).toLowerCase());
      });
    });
  }, [data, additionalFilter, filterableKeys]);

  const getSortArrow = sortKey => {
    if (!sortKey) return "";
    const [curField, curDirRaw] = String(filter?.sortBy || "none").split("|");
    const curDir = (curDirRaw || "").toUpperCase();
    if (curField !== sortKey) return "";
    return curDir === "DESC" ? " ▼" : curDir === "ASC" ? " ▲" : "";
  };

  const handleSort = sortKey => {
    if (!sortKey || !onFilterChange) return;
    const [curField, curDirRaw] = String(filter?.sortBy || "none").split("|");
    const curDir = (curDirRaw || "").toUpperCase();
    const nextSortBy = curField !== sortKey ? `${sortKey}|DESC` : curDir === "DESC" ? `${sortKey}|ASC` : curDir === "ASC" ? "none" : `${sortKey}|DESC`;
    onFilterChange({...filter, sortBy: nextSortBy, page: 1});
  };

  return (
    <table className="table table-striped" style={{borderCollapse: "collapse", width: "100%", textAlign: "center"}}>
      <thead>
        <tr className="headers-stick-trxs">
          {headers.map(h => {
            const canFilter = Object.prototype.hasOwnProperty.call(additionalFilter, h.key);
            const inputValue = additionalFilter[h.key] || "";
            return (
              <th key={h.key} style={{padding: "4px", border: "1px solid #ccc", whiteSpace: "nowrap", verticalAlign: "middle"}}>
                <div className="d-flex flex-column align-items-center justify-content-center">
                  <div className="font-weight-bold text-nowrap" onClick={() => handleSort(h.sortKey)} style={{cursor: h.sortKey ? "pointer" : "default", userSelect: "none"}}>
                    {h.name}{getSortArrow(h.sortKey)}
                  </div>

                  {canFilter && (
                    <div className="d-flex align-items-center justify-content-center" style={{width: "100%", minWidth: "90px", maxWidth: "130px"}}>
                      <input value={inputValue} onChange={e => {const value = e.target.value; onAdditionalFilterChange(prev => ({...prev, [h.key]: value}));}} style={{backgroundColor: inputValue ? "#d1d5db" : "#f8f9fa", border: inputValue ? "2px solid #634b4b" : "1px solid #ccc", boxShadow: inputValue ? "0 0 0 2px rgba(75,85,99,.15)" : "none", fontWeight: inputValue ? "600" : "normal", color: inputValue ? "#111827" : "inherit", width: "100%", minWidth: "90px", textAlign: "center", padding: "2px 4px", fontSize: "10px", height: "22px"}} />
                    </div>
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>

      <tbody>
        {filteredData.length > 0 ? filteredData.map((row, r) => {
          const cells = getCells(row);
          return (
            <tr key={r}>
              {headers.map(h => {
                const cell = cells.find(c => c.key === h.key);
                return <td key={h.key} style={{border: "1px solid #ddd", padding: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "180px", verticalAlign: "middle"}}>{renderCell(cell)}</td>;
              })}
            </tr>
          );
        }) : (
          <tr><td colSpan={headers.length || 1} className="p-0">Brak wyników</td></tr>
        )}
      </tbody>
    </table>
  );
};